<template>
  <div class="book-trip-wrap">
    <div class="filter-outerblk">
      <div class="single-fieldsouter search-field">
        <v-text-field
          v-model="filter.searchquery"
          label="Trip ID / Name / Mobile"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </div>

      <!-- No Branch filter here: the Branch selector in the top bar already
           scopes every request through the `branch` header. -->
      <div class="single-fieldsouter">
        <v-autocomplete
          v-model="filter.serviceType"
          :items="serviceTypeList"
          label="Service Type"
          item-text="name"
          item-value="id"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
          @change="getTripListing"
        ></v-autocomplete>
      </div>

      <div class="single-fieldsouter">
        <v-menu
          v-model="dateModal"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="filter.date"
              label="Date"
              readonly
              v-on="on"
              outlined
              dense
              hide-details
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filter.date"
            color="#d59e1d"
            no-title
            @input="
              dateModal = false;
              getTripListing();
            "
          ></v-date-picker>
        </v-menu>
      </div>

      <div class="reset-outer">
        <v-btn class="lighter-btn" color="#ff9600" @click="getTripListing" text>
          <i class="fas fa-redo"></i>
        </v-btn>
      </div>
      <div class="reset-outer">
        <v-btn class="reset-btn" color="#ff9500" small @click="reset">Reset</v-btn>
      </div>
    </div>

    <div class="table-outerblk">
      <!-- v-overlay rather than the shared Loading component: the fixed-header
           table sets z-index on its sticky header, which a plain absolute
           overlay loses against. -->
      <v-overlay absolute :value="isLoading" :opacity="0.7" color="#ffffff" z-index="4">
        <v-progress-circular indeterminate :size="50" :width="5" color="#d59e1d"></v-progress-circular>
      </v-overlay>

      <v-simple-table fixed-header v-if="trips.length > 0">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Trip ID</th>
              <th class="text-left">Crew Details</th>
              <th class="text-left">Pickup Address</th>
              <th class="text-left">Drop Address</th>
              <th class="text-left">Pickup Time</th>
              <th class="text-left">Reporting Time</th>
              <th class="text-left">Booking</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="trip in trips"
              :key="trip.tripId"
              class="clickable-row"
              @click="openDetails(trip)"
            >
              <td>
                <div class="stack-cell">
                  <span class="strong-text">{{ trip.displayId || trip.tripId }}</span>
                  <span class="muted-text">{{ serviceTypeName(trip.serviceType) }}</span>
                </div>
              </td>
              <td>
                <CrewDetails
                  :employees="trip.employees"
                  :fallbackName="trip.name"
                  :fallbackMobile="trip.mobile"
                />
              </td>
              <td class="address-cell">{{ trip.source || "--" }}</td>
              <td class="address-cell">{{ trip.destination || "--" }}</td>
              <td>{{ formatDate(trip.pickupDate) }}</td>
              <td>{{ formatDate(trip.reportingTime) }}</td>
              <td>
                <!-- Black with a car, matching the corporate portal's Request
                     Uber button - the same action should look the same in both. -->
                <v-btn
                  v-if="!trip.uberBooked"
                  small
                  dark
                  color="#000000"
                  class="book-btn"
                  @click.stop="openBooking(trip)"
                >
                  <v-icon size="15" class="mr-1">mdi-car</v-icon>
                  Request Uber
                </v-btn>
                <!-- A safety net rather than an expected state: the server
                     excludes every trip carrying an Uber booking from this tab,
                     so a row showing this tag means unexpected data. -->
                <span v-else class="booked-tag">
                  <i class="fas fa-check-circle"></i>&nbsp; Uber Booked
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div class="empty-state" v-else-if="!isLoading">
        <i class="fas fa-taxi"></i>
        <span>No trips waiting to be booked.</span>
      </div>
    </div>

    <div class="pagination" v-if="total > 0">
      <span class="count-text">{{ total }} trips</span>
      <v-pagination
        v-model="page"
        :length="pageLength"
        :total-visible="6"
        :disabled="isLoading"
        @input="fetchTrips"
      ></v-pagination>
    </div>

    <!-- ------------------------------------------------------------------ -->
    <!-- Uber estimate dialog - same theme as the corporate portal           -->
    <!-- ------------------------------------------------------------------ -->
    <v-dialog v-model="estimateDialog" width="860" persistent content-class="uber-estimate-dialog">
      <v-card class="uber-estimate-card" elevation="0">
        <div class="uep-header">
          <div class="uep-header-left">
            <div class="uep-uber-wordmark">
              <img :src="require('@/assets/images/uber/uberLogo.webp')" alt="Uber" class="uep-wordmark-img" />
            </div>
            <div class="uep-header-divider"></div>
            <div>
              <div class="uep-title">Choose a ride</div>
              <div class="uep-subtitle" v-if="currentBookingTrip">
                <div class="uep-subtitle-row">
                  <v-icon size="13" color="rgba(255,255,255,0.6)">mdi-map-marker</v-icon>
                  <span>{{ summaryPickup }}</span>
                </div>
                <div class="uep-subtitle-row">
                  <v-icon size="13" color="rgba(255,255,255,0.6)">mdi-map-marker-check</v-icon>
                  <span>{{ summaryDrop }}</span>
                </div>
                <!-- Uber validates this number before an estimate runs and can
                     refuse on it, so the agent needs to see which number the
                     refusal is about. -->
                <div class="uep-subtitle-row" v-if="summaryCrew">
                  <v-icon size="13" color="rgba(255,255,255,0.6)">mdi-phone-outline</v-icon>
                  <span>{{ summaryCrew }}</span>
                </div>
              </div>
            </div>
          </div>
          <v-btn icon dark @click="closeBooking" class="uep-close-btn">
            <v-icon size="22">mdi-close</v-icon>
          </v-btn>
        </div>

        <!--
          Pickup time is taken from the trip and is deliberately read-only -
          the trip owns the schedule, the booking screen must not drift from it.
          It is sent on the estimate call, which is what makes Uber return
          Reserve products at all.
        -->
        <!-- Not styled as an error when the slot has passed: it is a supported
             case now, and the note below says what happens instead. -->
        <div class="uep-schedule">
          <div class="uep-schedule-left">
            <v-icon size="16" color="#1a1a1a">mdi-calendar-clock</v-icon>
            <span class="uep-schedule-label">Pickup</span>
            <span class="uep-schedule-value">{{ pickupLabel }}</span>
          </div>
          <span class="uep-schedule-lead" v-if="leadMinutes !== null">
            <template v-if="pickupInPast">{{ leadText }} · On Demand</template>
            <template v-else>{{ leadText }} · {{ isNowish ? "On Demand" : "Scheduled" }}</template>
          </span>
        </div>

        <!--
          Says what will actually happen, in plain words - the same note the
          re-book dialog shows, because the outcome is the same. "On demand" is
          Uber's term, not something ops needs to learn.
        -->
        <div class="uep-note" :class="{ 'uep-note--now': sendNow }">
          <v-icon size="15" :color="sendNow ? '#0f766e' : '#8a6d1f'">
            {{ sendNow ? "mdi-car-clock" : "mdi-calendar-check" }}
          </v-icon>
          <span v-if="pickupInPast">
            This pickup time has already passed, so the cab will be sent
            <strong>right away</strong>.
          </span>
          <span v-else-if="sendNow">
            The pickup is close, so the cab will be sent <strong>right away</strong>.
          </span>
          <span v-else>
            The cab will be arranged for the pickup time above.
          </span>
        </div>

        <div v-if="estimating" class="uep-loading">
          <v-progress-circular indeterminate color="#000" size="44" width="3"></v-progress-circular>
          <p class="uep-loading-title">{{ verifyingPhone ? "Verifying mobile number" : "Finding available rides" }}</p>
          <p class="uep-loading-sub">{{ verifyingPhone ? "Please wait a moment…" : "This won't take long" }}</p>
        </div>

        <!--
          Ahead of the product list on purpose. Once the estimate has run out,
          what it described is no longer what would be booked, so the list must
          not be there to click.
        -->
        <div v-else-if="estimateExpired" class="uep-empty uep-empty--error">
          <v-icon size="56" color="#e5a3a3">mdi-timer-off-outline</v-icon>
          <p class="uep-empty-title">This estimate has expired</p>
          <p class="uep-empty-sub">Press Refresh to see current options.</p>
        </div>

        <div v-else-if="uberProducts.length > 0" class="uep-list">
          <div
            v-for="product in uberProducts"
            :key="product.productId"
            class="uep-row"
            :class="{
              'uep-row--selected': selectedProduct === product.productId,
              'uep-row--blocked': !eligibility(product).bookable,
            }"
            @click="selectProduct(product)"
          >
            <div
              class="uep-img-wrap"
              :style="product.backGroundImage ? `background-image:url('${product.backGroundImage}')` : ''"
            >
              <img v-if="product.image" :src="product.image" :alt="product.category" class="uep-vehicle-img" />
              <v-icon v-else size="44" color="#aaa">mdi-car</v-icon>
            </div>

            <div class="uep-info">
              <div class="uep-name-row">
                <span class="uep-name">{{ product.category }}</span>
                <span class="uep-badge" :class="`uep-badge--${bookingTypeOf(product)}`">
                  {{ bookingTypeLabel(product) }}
                </span>
              </div>

              <div class="uep-meta">
                <span class="uep-meta-item" v-if="product.capacity">
                  <v-icon size="12" color="#6b6b6b">mdi-account-multiple-outline</v-icon>
                  {{ product.capacity }} seats
                </span>
                <span class="uep-meta-dot" v-if="product.capacity"></span>
                <span class="uep-meta-item">
                  <v-icon size="12" color="#6b6b6b">mdi-map-marker-distance</v-icon>
                  {{ distanceOf(product) }}
                </span>
                <span class="uep-meta-dot"></span>
                <span class="uep-meta-item">
                  <v-icon size="12" color="#6b6b6b">mdi-clock-outline</v-icon>
                  {{ durationOf(product) }}
                </span>
              </div>

              <div class="uep-time-rows">
                <div class="uep-eta-row">
                  <v-icon size="13" color="#6b6b6b">mdi-clock-start</v-icon>
                  <span>Pickup By: <strong>{{ product.pickupEstimate || "---" }}</strong></span>
                </div>
                <div class="uep-eta-row">
                  <v-icon size="13" color="#276EF1">mdi-navigation-variant</v-icon>
                  <span>Drop By: <strong>{{ (product.trip && product.trip.eta) || "---" }}</strong></span>
                </div>
              </div>

              <div class="uep-reserve" v-if="reserveNote(product)">{{ reserveNote(product) }}</div>

              <div class="uep-block-note" v-if="!eligibility(product).bookable">
                <v-icon size="12" color="#c62828">mdi-alert-circle-outline</v-icon>
                {{ eligibility(product).reasons[0] }}
              </div>
            </div>

            <div class="uep-check" :class="{ 'uep-check--on': selectedProduct === product.productId }">
              <v-icon size="18" color="white">mdi-check</v-icon>
            </div>
          </div>
        </div>

        <!--
          Two different outcomes, said differently.

          A failed estimate is not "no cars nearby" - reporting it that way sends
          the operator looking at Uber supply when the real problem is something
          they can fix, like a rejected phone number. Only an estimate that
          actually came back empty says no rides.
        -->
        <div v-else-if="estimateError" class="uep-empty uep-empty--error">
          <v-icon size="56" color="#e5a3a3">mdi-alert-circle-outline</v-icon>
          <p class="uep-empty-title">Could not load rides</p>
          <p class="uep-empty-sub">{{ estimateError }}</p>
        </div>

        <div v-else class="uep-empty">
          <v-icon size="56" color="#d0d0d0">mdi-car-off</v-icon>
          <p class="uep-empty-title">No rides available</p>
          <p class="uep-empty-sub">Uber has no vehicles for this route right now.</p>
        </div>

        <div class="uep-footer">
          <!-- One clock, counting to the earliest of Uber's quote expiry, our
               five-minute cap, and the pickup crossing into the on-demand
               window. Turns red in the last minute rather than only at zero. -->
          <span class="uep-footer-note" v-if="estimateExpired">
            This estimate has expired — press Refresh.
          </span>
          <span
            class="uep-footer-note"
            :class="{ 'uep-footer-note--urgent': estimateSecondsLeft <= 60 }"
            v-else-if="estimateCountdown"
          >
            <v-icon size="13" :color="estimateSecondsLeft <= 60 ? '#c62828' : '#8a8a8a'">
              mdi-timer-outline
            </v-icon>
            Estimate valid for {{ estimateCountdown }}
          </span>
          <v-spacer></v-spacer>
          <v-btn text class="uep-btn-cancel" @click="closeBooking">Cancel</v-btn>
          <v-btn text class="uep-btn-cancel" :loading="estimating" @click="getUberEstimate">Refresh</v-btn>
          <v-btn
            class="uep-btn-confirm"
            :loading="booking"
            :disabled="!canBook"
            @click="createUberTrip"
            elevation="0"
          >
            Confirm Ride
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <Confirm ref="confirm"></Confirm>
  </div>
</template>

<script>
import axios from "@/axios/axios-global";
import moment from "moment";

// Must match ESTIMATE_MAX_AGE_MS in routes/web/uber/model.js. The server is what
// enforces it; this only decides when the countdown on screen runs out, so the
// agent is told before Confirm is refused rather than after.
const ESTIMATE_MAX_AGE_MS = 5 * 60 * 1000;
import toast from "@/components/common/toast";
import { readApiError } from "./apiError";
import Confirm from "./UberConfirm.vue";
import CrewDetails from "./CrewDetails.vue";

export default {
  name: "CreateTrip",
  components: { Confirm, CrewDetails },
  props: {
    rebookFrom: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      filter: {
        searchquery: "",
        serviceType: null,
        date: moment().format("YYYY-MM-DD"),
      },
      serviceTypeList: [
        { id: 1, name: "Pickup" },
        { id: 2, name: "Drop off" },
      ],
      trips: [],
      isLoading: false,
      dateModal: false,
      searchTimer: null,
      page: 1,
      pageSize: 50,
      total: 0,

      estimateDialog: false,
      currentBookingTrip: null,
      uberProducts: [],
      selectedProduct: null,
      estimating: false,
      verifyingPhone: false,
      estimateError: "",
      booking: false,

      // Scheduling verdict from the server - pickup time, lead minutes, mode.
      schedule: null,
      // The server clock reading that stamped the current estimate.
      estimatedAt: null,
      // serverTime minus the local clock at the moment of the response, so
      // fare countdowns tick against server time even on a skewed machine.
      serverOffset: 0,

      now: Date.now(),
      expiryTimer: null,
    };
  },
  computed: {
    pageLength() {
      return this.pageSize > 0 ? Math.ceil(this.total / this.pageSize) : 1;
    },

    // The dialog is fed from two shapes: a bookable row (name/mobile/source/
    // destination) and a re-booked trip off the Attention tab (guest/pickup/
    // dropoff). Read both so the summary is never blank.
    summaryPickup() {
      const trip = this.currentBookingTrip || {};
      return trip.source || (trip.pickup && trip.pickup.address) || "Pickup";
    },
    summaryDrop() {
      const trip = this.currentBookingTrip || {};
      return trip.destination || (trip.dropoff && trip.dropoff.address) || "Drop";
    },

    /**
     * Who the ride is for, and on which number.
     *
     * The number is the part that matters: Uber checks it before returning an
     * estimate and refuses outright if it is a landline or has opted out of
     * their SMS. Without it on screen, "The crew number is not a mobile phone"
     * leaves the agent guessing which number to go and correct.
     *
     * Both shapes are read - a To be Booked row carries name/mobile, a re-book
     * off the Attention tab carries the booking's guest.
     */
    summaryCrew() {
      const trip = this.currentBookingTrip || {};
      const guest = trip.guest || {};

      const name = trip.name || `${guest.firstName || ""} ${guest.lastName || ""}`.trim();
      const mobile = trip.mobile || guest.phoneNumber || "";

      return [name, mobile].filter(Boolean).join(" · ");
    },

    /** Pickup time owned by the trip. Read-only here by design. */
    pickupAt() {
      const trip = this.currentBookingTrip || {};
      return trip.pickupDate || trip.pickupTime || null;
    },
    pickupAtMs() {
      if (this.schedule && this.schedule.pickupTime) return this.schedule.pickupTime;
      const parsed = this.toMoment(this.pickupAt);
      return parsed && parsed.isValid() ? parsed.valueOf() : null;
    },
    /** Server-rendered pickup label; falls back to local formatting pre-estimate. */
    pickupLabel() {
      if (this.schedule && this.schedule.pickupDisplay) return this.schedule.pickupDisplay;
      return this.formatDate(this.pickupAt);
    },
    leadMinutes() {
      // Straight off the server. The browser clock may be wrong or in another
      // timezone, and must not decide how - or whether - a trip is booked.
      if (this.schedule && this.schedule.leadMinutes !== null && this.schedule.leadMinutes !== undefined) {
        return this.schedule.leadMinutes;
      }
      return null;
    },
    leadText() {
      const minutes = this.leadMinutes;
      if (minutes === null) return "";
      if (minutes < 0) return `${Math.abs(minutes)} min ago`;
      if (minutes < 60) return `in ${minutes} min`;
      return `in ${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    },

    /** The pickup slot has already gone by, so the ride goes out immediately. */
    pickupInPast() {
      return !!(this.schedule && this.schedule.inPast);
    },

    /** Whether the server decided this dispatches now rather than scheduled. */
    isNowish() {
      if (!this.schedule) return false;
      return this.schedule.isNowish === true;
    },

    /**
     * Whether the cab goes out immediately - either the pickup is minutes away
     * or it has already passed. Both sources are server-computed: `dispatchNow`
     * once an estimate has run, and `pickupPassed` from the listing before that.
     * The browser clock is never consulted.
     */
    sendNow() {
      if (this.schedule && this.schedule.dispatchNow !== undefined) {
        return this.schedule.dispatchNow === true;
      }
      return !!(this.currentBookingTrip && this.currentBookingTrip.pickupPassed);
    },

    onDemandWindow() {
      return (this.schedule && this.schedule.onDemandWindowMinutes) || 30;
    },

    selectedProductData() {
      return this.uberProducts.find((item) => item.productId === this.selectedProduct) || null;
    },
    fareExpired() {
      return this.uberProducts.length > 0 && this.uberProducts.every((p) => this.isProductExpired(p));
    },

    /**
     * The moment this estimate stops describing the booking it was fetched for.
     *
     * The earliest of three deadlines, so one countdown covers all of them:
     *
     *   1. the soonest quote Uber stamped - expires_at, or a Reserve's
     *      valid_until_timestamp
     *   2. five minutes from the fetch. The only guard on a product returned
     *      with fares_unavailable, which carries no expiry at all
     *   3. the moment the pickup crosses into the on-demand window. Past that
     *      the server dispatches immediately, so an estimate requested with a
     *      scheduling block is describing a different booking
     *
     * The third is knowable in advance, which is what lets the countdown simply
     * end there rather than the screen rewriting itself under the agent.
     */
    estimateValidUntil() {
      if (!this.schedule || this.uberProducts.length === 0) return null;

      const deadlines = [];

      this.uberProducts.forEach((product) => {
        const fare = this.quoteDeadline(product.expiresAt, "expires_at");
        if (fare) deadlines.push(fare);
        const reserve =
          product.reserveInfo && this.quoteDeadline(product.reserveInfo.validUntilMs, "valid_until");
        if (reserve) deadlines.push(reserve);
      });

      if (this.estimatedAt) deadlines.push(this.estimatedAt + ESTIMATE_MAX_AGE_MS);

      // Only while the trip is still an advance booking - once it dispatches on
      // demand there is no window left to cross.
      if (!this.sendNow && this.schedule.pickupTime) {
        deadlines.push(Number(this.schedule.pickupTime) - this.onDemandWindow * 60000);
      }

      return deadlines.length > 0 ? Math.min(...deadlines) : null;
    },

    estimateSecondsLeft() {
      if (!this.estimateValidUntil) return null;
      return Math.max(0, Math.round((this.estimateValidUntil - this.serverNow()) / 1000));
    },

    estimateExpired() {
      return this.estimateSecondsLeft === 0;
    },

    /** mm:ss, so a countdown reads as one at a glance. */
    estimateCountdown() {
      const seconds = this.estimateSecondsLeft;
      if (seconds === null) return "";
      const minutes = Math.floor(seconds / 60);
      return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
    },
    canBook() {
      // A passed pickup is bookable - it dispatches on demand rather than being
      // scheduled. The note above the list says so before anything is chosen.
      //
      // An expired estimate is not: the server refuses it anyway, and Confirm
      // should not look available when it is going to be turned down.
      if (this.estimateExpired) return false;
      if (!this.selectedProductData || this.estimating || this.booking) return false;
      return this.eligibility(this.selectedProductData).bookable;
    },
  },
  watch: {
    "filter.searchquery"(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (!val || val.length === 0 || val.length >= 3) this.getTripListing();
      }, 400);
    },
    rebookFrom: {
      immediate: true,
      handler(trip) {
        if (trip) this.openBooking(trip);
      },
    },
  },
  mounted() {
    this.getTripListing();
    // Fare ids and Reserve quotes both expire, so the countdowns have to tick.
    this.expiryTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.expiryTimer) clearInterval(this.expiryTimer);
  },
  methods: {
    /* ---------------------------------------------------------------- *
     * Listing
     * ---------------------------------------------------------------- */

    listPayload(page) {
      return {
        search: this.filter.searchquery || "",
        flightNo: this.filter.searchquery || "",
        serviceType: this.filter.serviceType,
        startDate: this.filter.date,
        endDate: this.filter.date,
        date: this.filter.date,
        page,
        limit: this.pageSize,
        excludeBooked: true,
      };
    },

    mapRow(row) {
      return {
        tripId: row.bigId,
        displayId: row.id,
        name: row.user,
        mobile: row.mobile,
        source: row.source,
        destination: row.destination,
        pickupDate: row.pickUpTime,
        // Crew rows for the details cell: name, mobile, crewId, flightInfo.
        employees: row.employees || [],
        // First employee's reporting time, epoch ms - also the sort key.
        reportingTime: row.reportingTime,
        serviceType: row.serviceType,
        uberBooked: row.tripStatus === "Uber Booked",
        // Server-computed: the booking dialog needs to know the slot has gone by
        // before any estimate has run, and the browser clock cannot be trusted
        // to say so.
        pickupPassed: row.pickupPassed === true,
      };
    },

    /** Fetches the current page. Pagination replaces rows rather than appending. */
    async fetchTrips() {
      this.isLoading = true;
      try {
        const response = await axios.post("uber/trip/list", this.listPayload(this.page));
        const data = response.data || {};
        this.trips = (data.list || []).map(this.mapRow);

        // The server only counts on page 1 - it costs as much as the page
        // itself. Later pages omit it, so keep the total we already have.
        if (data.total !== null && data.total !== undefined) this.total = data.total;
      } catch (e) {
        this.trips = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    /** Any filter change resets to the first page. */
    getTripListing() {
      this.page = 1;
      return this.fetchTrips();
    },

    /* ---------------------------------------------------------------- *
     * Booking
     * ---------------------------------------------------------------- */

    /**
     * Opens the dialog and estimates straight away - there is nothing for ops
     * to fill in first, because the trip already carries the guest, the route
     * and the pickup time.
     */
    openDetails(trip) {
      this.$emit("open-details", trip.tripId);
    },

    openBooking(trip) {
      this.currentBookingTrip = trip;
      this.estimateDialog = true;
      this.uberProducts = [];
      this.selectedProduct = null;
      this.estimateError = "";
      this.schedule = null;
      this.estimatedAt = null;
      this.now = Date.now();
      // Whether the pickup has passed is the server's call, not the browser's,
      // so the estimate is always attempted and the server may refuse it.
      this.getUberEstimate();
    },

    closeBooking() {
      this.estimateDialog = false;
      this.currentBookingTrip = null;
      this.uberProducts = [];
      this.selectedProduct = null;
      this.estimateError = "";
      this.schedule = null;
      this.estimatedAt = null;
      this.$emit("clear-rebook");
    },

    async getUberEstimate() {
      if (!this.currentBookingTrip) return;
      this.estimating = true;
      this.estimateError = "";
      this.uberProducts = [];
      this.selectedProduct = null;

      try {
        // Guest mobile is validated first, exactly as the corporate portal
        // does - a bad number fails the booking later, not here.
        try {
          this.verifyingPhone = true;
          await axios.post("uber/phone-info", { tripId: this.currentBookingTrip.tripId });
        } finally {
          this.verifyingPhone = false;
        }

        // No time is sent: the server reads the trip's pickup time and decides
        // on-demand vs scheduled itself.
        const response = await axios.post("uber/trip/estimate", {
          tripId: this.currentBookingTrip.tripId,
        });

        const data = response.data || {};
        const list = Array.isArray(data) ? data : data.products || [];
        this.uberProducts = list;
        this.schedule = data.schedule || null;

        // Align the local clock to the server so fare countdowns are honest
        // even if this machine is minutes out.
        if (this.schedule && this.schedule.serverTime) {
          this.serverOffset = this.schedule.serverTime - Date.now();
        }

        // The server's own clock reading when it built this estimate. Sent back
        // on create so the server can judge the estimate's age against the same
        // clock that stamped it.
        this.estimatedAt = (this.schedule && this.schedule.serverTime) || null;
        this.now = Date.now();

        const bookable = list.filter((item) => this.eligibility(item).bookable);
        if (bookable.length === 1) this.selectedProduct = bookable[0].productId;
      } catch (err) {
        this.uberProducts = [];
        this.estimateError = this.readError(err, "Could not load rides for this trip.");
      } finally {
        this.estimating = false;
      }
    },

    selectProduct(product) {
      if (!this.eligibility(product).bookable) return;
      this.selectedProduct = product.productId;
    },

    async createUberTrip() {
      const product = this.selectedProductData;
      if (!product || !this.currentBookingTrip) return;

      if (this.isProductExpired(product)) {
        toast.error(`${product.category} is no longer available. Press Refresh to reload the rides.`);
        return;
      }

      // A ride going out now must not be confirmed against a pickup time that
      // has passed - it would read as booking a cab for last night.
      const ride = product.category || "Uber";
      const trip = this.currentBookingTrip.displayId || this.currentBookingTrip.tripId;
      const confirmed = await this.$refs.confirm.open(
        "Request Uber",
        this.sendNow
          ? `Request ${ride} for trip #${trip}? The cab will be sent right away.`
          : `Request ${ride} for trip #${trip}, pickup ${this.pickupLabel}?`,
        { icon: "fas fa-car", color: "#000000", buttonName: "Request" }
      );
      if (!confirmed) return;

      this.booking = true;
      try {
        const payload = {
          tripId: this.currentBookingTrip.tripId,
          productId: product.productId,
          // Uber's own name for the class, sent back with the id it came with -
          // the server stores the pair, so a booking reads as a vehicle class
          // rather than an opaque id.
          vehicleType: product.displayName || null,
          pickupEta: product.pickupEta || 0,
          dropEta: (product.trip && product.trip.dropEta) || 0,
        };
        // Mode follows the server's scheduling verdict first, then the product:
        // a pickup that is close or already passed dispatches now; further out a
        // RESERVE class books as Reserve and anything else against the scheduled
        // time. The server re-derives this from its own clock before calling
        // Uber - this is a hint, not the authority.
        if (this.schedule && !this.sendNow && this.schedule.pickupTime) {
          payload.pickupTime = this.schedule.pickupTime;
          payload.mode = this.bookingTypeOf(product) === "RESERVE" ? "reserve" : "scheduled";
        } else {
          payload.mode = "on_demand";
        }
        if (product.fareId) payload.fareId = product.fareId;
        if (this.rebookFrom) payload.reBooking = true;

        // Which estimate this booking is being made from, so the server can
        // judge whether it still describes the booking - see staleEstimate().
        if (this.estimatedAt) payload.estimatedAt = this.estimatedAt;
        if (this.schedule) payload.dispatchNow = this.sendNow;

        const response = await axios.post("uber/trip/create", payload);
        toast.success((response.data && response.data.message) || "Uber trip created successfully.");

        const index = this.trips.findIndex((item) => item.tripId === this.currentBookingTrip.tripId);
        if (index !== -1) this.$set(this.trips[index], "uberBooked", true);

        this.estimateDialog = false;
        this.$emit("created", response.data);
      } catch (err) {
        toast.error(this.readError(err, "Error creating Uber trip."));
      } finally {
        this.booking = false;
      }
    },

    /* ---------------------------------------------------------------- *
     * Product presentation and eligibility
     * ---------------------------------------------------------------- */

    bookingTypeOf(product) {
      const value = String(product.advanceBookingType || "").toUpperCase();
      return ["RESERVE", "SCHEDULED", "FLEXIBLE"].includes(value) ? value : "ONDEMAND";
    },

    bookingTypeLabel(product) {
      const type = this.bookingTypeOf(product);
      return type === "ONDEMAND" ? "ON DEMAND" : type;
    },

    distanceOf(product) {
      const trip = product.trip || {};
      const value =
        trip.travelDistanceEstimate !== undefined && trip.travelDistanceEstimate !== null && trip.travelDistanceEstimate !== ""
          ? trip.travelDistanceEstimate
          : trip.distanceEstimate;
      if (value === undefined || value === null || value === "") return "--";
      return `${value} ${trip.distanceUnit || ""}`.trim();
    },

    durationOf(product) {
      const seconds = (product.trip || {}).durationEstimate;
      if (!seconds) return "--";
      return `${Math.round(seconds / 60)} min`;
    },

    reserveNote(product) {
      if (this.bookingTypeOf(product) !== "RESERVE" || !product.reserveInfo) return "";
      const info = product.reserveInfo;
      const bits = [];
      if (info.scheduledThresholdMinutes !== undefined && info.scheduledThresholdMinutes !== null) {
        bits.push(`book ${info.scheduledThresholdMinutes} min ahead`);
      }
      if (info.freeCancellationThresholdMinutes !== undefined && info.freeCancellationThresholdMinutes !== null) {
        bits.push(`free cancellation until ${info.freeCancellationThresholdMinutes} min before pickup`);
      }
      return bits.length > 0 ? `Reserve · ${bits.join(" · ")}` : "";
    },

    /** Local clock corrected to the server's. */
    serverNow() {
      return this.now + this.serverOffset;
    },

    isProductExpired(product) {
      // Guarded like the others: a fare that "expired" before the estimate was
      // issued is a malformed value, and would refuse the row on every load.
      const expiry = this.quoteDeadline(product.expiresAt, "expires_at");
      return !!expiry && this.serverNow() >= expiry;
    },

    /**
     * Whether a product can be booked for this trip's pickup time. Every
     * threshold comes from the API response - nothing is hardcoded.
     */
    eligibility(product) {
      const reasons = [];

      // A passed pickup is not a blocker: it dispatches on demand, so every
      // product that could be sent now is bookable.
      if (!product.productId) reasons.push("No product id returned for this ride.");
      if (this.isProductExpired(product)) reasons.push("This option has expired — press Refresh.");

      const type = this.bookingTypeOf(product);

      if (this.sendNow) {
        // The ride goes out immediately - because the pickup is close, or
        // because it has already passed - so an advance-only product still
        // books, it simply books immediately. None of the lead-time rules below
        // apply when there is nothing left to schedule.
      } else {
        /*
         * Whether a vehicle can hold this pickup is Uber's answer, not ours.
         *
         * The estimate request carries scheduling.pickup_time, so every product
         * in the response was already judged against this exact time and
         * `advanceBookingType` is that judgement. Re-deriving it here - against
         * reserve_info.scheduled_threshold_minutes, or against
         * scheduling_enabled - only ever produced false negatives: a car Uber
         * would have given us, refused by us. Do not add such a rule back.
         *
         * The only thing read here is advanceBookingType itself: no advance type
         * means Uber offered this class for an immediate pickup only.
         */
        if (type === "ONDEMAND") {
          reasons.push("This vehicle is only offered for an immediate pickup.");
        }
      }

      // Same guard as the countdown: a valid_until that predates the estimate is
      // a malformed value, not an expiry, and must not refuse every row.
      const validUntil = product.reserveInfo && this.quoteDeadline(product.reserveInfo.validUntilMs, "valid_until");
      if (validUntil && validUntil < this.serverNow()) {
        reasons.push("This Reserve option has expired — press Refresh.");
      }

      return { bookable: reasons.length === 0, reasons };
    },

    /* ---------------------------------------------------------------- *
     * Helpers
     * ---------------------------------------------------------------- */

    /**
     * Uber sends timestamps as epoch seconds in some responses and ISO in
     * others. Treating seconds as milliseconds puts the value in 1970 and every
     * fare would read as expired, so normalise both.
     */
    /**
     * A quote deadline from Uber, but only if it can actually be one.
     *
     * A timestamp that had already passed when the estimate arrived is not an
     * expiry - Uber does not issue a quote that expired before it was created.
     * It means the field held something other than an absolute time: a duration
     * in seconds, say, which toMillis turns into an epoch in 1970 and which
     * would then expire every estimate the instant it loaded.
     *
     * Discarded rather than trusted, with a warning naming the field so the bad
     * value can be identified in the console instead of silently blocking every
     * booking.
     */
    quoteDeadline(value, field) {
      const ms = this.toMillis(value);
      if (!ms) return null;

      // Compared against the estimate's own timestamp, not "now" - a deadline a
      // few seconds out is legitimate, one predating the estimate is not.
      const issuedAt = this.estimatedAt || this.serverNow();
      if (ms <= issuedAt) {
        console.warn(
          `[Uber] Ignoring ${field}: ${value} resolves to ${new Date(ms).toISOString()}, ` +
            `before the estimate was issued. Treating it as no expiry.`
        );
        return null;
      }

      return ms;
    },

    toMillis(value) {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "number" || /^\d+$/.test(String(value))) {
        const num = Number(value);
        return num < 1e12 ? num * 1000 : num;
      }
      const parsed = moment(value);
      return parsed.isValid() ? parsed.valueOf() : null;
    },

    /** pickUpTime comes off time.pickupdate as an epoch value. */
    toMoment(value) {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "number" || /^\d+$/.test(String(value))) {
        return moment(Number(value));
      }
      const parsed = moment(value);
      return parsed.isValid() ? parsed : null;
    },

    serviceTypeName(id) {
      const found = this.serviceTypeList.find((item) => item.id === id);
      return found ? found.name : "";
    },

    formatDate(value) {
      const parsed = this.toMoment(value);
      return parsed && parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "--";
    },

    reset() {
      this.filter = {
        searchquery: "",
        serviceType: null,
        date: moment().format("YYYY-MM-DD"),
      };
      this.getTripListing();
    },

    readError(err, fallback) {
      return readApiError(err, fallback);
    },
  },
};
</script>

<style lang="scss" scoped>
// Fills whatever height the tab shell gives it - no assumption about the
// surrounding chrome.
.book-trip-wrap {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  // Measured and published by UberTrips; the calc is only for the first paint.
  height: var(--uber-tab-height, calc(100vh - 190px));
  box-sizing: border-box;
  overflow: hidden;
}

.filter-outerblk {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  flex-shrink: 0;
}

.single-fieldsouter {
  display: flex;
  align-items: center;
  flex: 1 1 190px;
  max-width: 230px;
}

.search-field {
  flex: 1 1 280px;
  max-width: 320px;
}

.reset-outer {
  display: flex;
  align-items: center;
}

.reset-btn {
  font-weight: 600;
}

.table-outerblk {
  margin-top: 14px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  overflow: hidden;
  position: relative;
  // Takes whatever height is left between the filter bar and the pagination,
  // rather than a fixed calc that has to be kept in sync by hand.
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  // Let the Vuetify table stretch to the panel and scroll inside it, so the
  // sticky header stays put and the page itself never scrolls.
  ::v-deep .v-data-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  th {
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6a6a6a !important;
    white-space: nowrap;
  }

  td {
    font-size: 13px;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.clickable-row {
  cursor: pointer;
}

.stack-cell {
  display: flex;
  flex-direction: column;
}

.strong-text {
  font-weight: 600;
  color: #333333;
}

.muted-text {
  font-size: 11px;
  color: #8a8a8a;
}

.address-cell {
  max-width: 240px;
  white-space: normal;
}

// Mirrors .uber-action-btn.book in the corporate portal's Last Minute Pullout.
.book-btn {
  color: #ffffff !important;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-radius: 4px;
  white-space: nowrap;
  background: linear-gradient(135deg, #1a1a1a 0%, #3a3a3a 100%) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35) !important;
}

.booked-tag {
  display: inline-flex;
  align-items: center;
  color: #2e9e5b;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  // Fills the panel so the message sits in the middle rather than at the top.
  flex: 1;
  color: #b0b0b0;
  font-size: 14px;

  i {
    font-size: 32px;
  }
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  margin-top: 14px;
  padding: 6px 16px;
  flex-shrink: 0;
}

.count-text {
  position: absolute;
  left: 16px;
  font-size: 12px;
  color: #8a8a8a;
}

/* ------------------------------------------------------------------ *
 * Uber estimate dialog - mirrors the corporate portal theme
 * ------------------------------------------------------------------ */

::v-deep .uber-estimate-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.22) !important;
}

/*
 * Bounded by the screen, not by a guess at one.
 *
 * The card used to size itself from its contents - a 440px ride list plus the
 * header, the pickup bar, the notice and the footer, near enough 700px before
 * the browser's own chrome. On a 768px laptop that overflowed, and because the
 * dialog above carries `overflow: hidden` the excess was CLIPPED rather than
 * scrolled: Confirm Ride was not below the fold, it was not rendered. Only
 * zooming out brought it back, which is what made it look like a monitor issue.
 *
 * A column capped at 90vh instead: the header and footer hold their size, and
 * the ride list absorbs whatever is left over.
 */
.uber-estimate-card {
  border-radius: 16px !important;
  overflow: hidden;
  font-family: "UberMove", "UberMoveText", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #fff;

  display: flex;
  flex-direction: column;
  max-height: 90vh;
  min-height: 0;
}

.uep-header {
  /* Holds its size; the card scrolls its middle, not its edges. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #000;
  padding: 14px 18px;
}

.uep-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.uep-uber-wordmark {
  display: flex;
  align-items: center;
}

.uep-wordmark-img {
  height: 36px;
  width: auto;
  display: block;
  object-fit: contain;
}

.uep-header-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
}

.uep-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  letter-spacing: -0.5px;
}

.uep-subtitle {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 4px;
}

.uep-subtitle-row {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;

  span {
    flex: 1;
  }

  .v-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.uep-close-btn {
  opacity: 0.6 !important;
  transition: opacity 0.15s !important;
}

.uep-close-btn:hover {
  opacity: 1 !important;
}

/* Read-only schedule strip */
.uep-schedule {
  /* Fixed: the pickup time stays in view while the list scrolls. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 18px;
  background: #f7f7f7;
  border-bottom: 1px solid #ebebeb;
}

.uep-schedule-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

// Same treatment as the re-book dialog's note, so the two read as one product.
.uep-note {
  /* Fixed: this is the warning that the cab goes right away. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  font-size: 12px;
  background: #fffaf0;
  color: #8a6d1f;
  border-bottom: 1px solid #ebebeb;

  strong {
    font-weight: 700;
  }

  &--now {
    background: #f0fdf9;
    color: #0f766e;
  }
}

.uep-schedule-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a8a8a;
  font-weight: 600;
}

.uep-schedule-value {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.2px;
}

.uep-schedule-lead {
  font-size: 12px;
  color: #6b6b6b;
}

.uep-loading {
  /* Shares the list's slot, so the footer must stay put here too. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 12px;
  background: #fff;
}

.uep-loading-title {
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0;
  letter-spacing: -0.4px;
}

.uep-loading-sub {
  font-size: 13px;
  color: #6b6b6b;
  margin: 0;
}

.uep-list {
  background: #fff;
  padding: 8px 0 4px;

  /*
   * The one part that scrolls, and the one that gives way.
   *
   * min-height: 0 is load-bearing - a flex child will not shrink below its own
   * content without it, and the overflow comes straight back. The 440px stays
   * as a ceiling so the list does not sprawl on a tall monitor; with the card
   * capped above, it is no longer also a floor.
   */
  flex: 1 1 auto;
  min-height: 0;
  max-height: 440px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
}

.uep-row {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  cursor: pointer;
  transition: background 0.1s ease;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 18px;
    right: 18px;
    height: 1px;
    background: #f0f0f0;
  }

  &:hover {
    background: #fafafa;
  }

  &--selected {
    background: #f5f5f5 !important;

    .uep-name {
      color: #000;
    }
    .uep-check {
      background: #000;
    }
  }

  &--blocked {
    opacity: 0.55;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }
}

.uep-img-wrap {
  flex-shrink: 0;
  width: 110px;
  height: 64px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f6f6f6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 16px;
}

.uep-vehicle-img {
  width: 100px;
  height: 56px;
  object-fit: contain;
}

.uep-info {
  flex: 1;
  min-width: 0;
}

.uep-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uep-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.uep-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #ececec;
  color: #5a5a5a;
  white-space: nowrap;

  &--RESERVE {
    background: #e8eaf6;
    color: #3f51b5;
  }

  &--SCHEDULED {
    background: #e3f2fd;
    color: #1565c0;
  }

  &--FLEXIBLE {
    background: #f3e5f5;
    color: #7b1fa2;
  }
}

.uep-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.uep-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b6b6b;
  font-weight: 400;
}

.uep-meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #c8c8c8;
  flex-shrink: 0;
}

.uep-time-rows {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 5px;
}

.uep-eta-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #555;
  font-weight: 500;

  strong {
    font-weight: 700;
    color: #1a1a1a;
  }

  &:last-child {
    color: #276ef1;
    strong {
      color: #276ef1;
    }
  }
}

.uep-reserve {
  margin-top: 5px;
  font-size: 11px;
  color: #3f51b5;
}

.uep-block-note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  font-size: 11px;
  color: #c62828;
}

.uep-check {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;
  transition: background 0.15s ease;

  &--on {
    background: #000 !important;
  }

  .v-icon {
    opacity: 0.5;
  }

  &--on .v-icon {
    opacity: 1 !important;
  }
}

.uep-empty {
  /* Shares the list's slot. Matters most in the expired state, where Refresh
     in the footer is the only way out. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  background: #fff;
}

.uep-empty-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 14px 0 6px;
  letter-spacing: -0.3px;
}

.uep-empty-sub {
  font-size: 13px;
  color: #9e9e9e;
  margin: 0;
}

/* The failure variant: the server's own message is the point, so it carries the
   weight rather than sitting in the grey used for an ordinary empty result. */
.uep-empty--error {
  .uep-empty-sub {
    font-size: 14px;
    font-weight: 600;
    color: #c62828;
    max-width: 460px;
  }
}

.uep-empty-hint {
  font-size: 12px;
  color: #9e9e9e;
  margin: 10px 0 0;
}

.uep-footer {
  /* Never shrinks - Confirm Ride is the reason the dialog exists. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid #ebebeb;
  background: #fff;
}

/*
 * On a genuinely short screen, buy the list some rows back.
 *
 * The header is the tallest fixed part - a logo, a title and three address
 * lines. Capping the card at 90vh alone would leave a 600px screen showing two
 * rides; trimming the padding here is worth another one or two, which is the
 * difference between choosing a ride and scrolling to find one.
 */
@media (max-height: 720px) {
  .uep-header {
    padding: 9px 18px;
  }

  .uep-subtitle-row {
    line-height: 1.25;
  }

  .uep-schedule,
  .uep-note {
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .uep-footer {
    padding: 9px 18px;
  }
}

// Ordinary state is quiet - it is a clock, not a warning, until it nearly runs
// out.
.uep-footer-note {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #8a8a8a;
  font-weight: 600;
}

.uep-footer-note--urgent {
  color: #c62828;
}

.uep-btn-cancel {
  font-size: 14px !important;
  color: #6b6b6b !important;
  font-weight: 500 !important;
  letter-spacing: -0.2px !important;
  text-transform: none !important;
}

.uep-btn-confirm {
  background: #000 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  padding: 0 24px !important;
  height: 40px !important;
  letter-spacing: -0.3px !important;
  text-transform: none !important;
  min-width: 180px !important;
}
</style>
